import { LoadingSpinner } from "components/LoadingSpinner";
import useDocumentTitle from "hooks/useDocumentTitle";
import RuleHistory from "pages/PayerRules/components/RuleHistory";
import { useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useGetCaseRateRulesetByIdQuery } from "store/queries/pricing";

import { BilateralToggle } from "./components/BilateralToggle";
import { CaseRateConflictBanner } from "./components/CaseRateConflictBanner";
import { ContractLanguageField } from "./components/ContractLanguageField";
import { CptCodes } from "./components/CptCodes";
import { ExcludedCptCodes } from "./components/ExcludedCptCodes";
import { Header } from "./components/Header";
import { PerDiemToggle } from "./components/PerDiemToggle";
import { ProviderAndPayerEditSection } from "./components/ProviderAndPayerEditSection";
import { CodeListFamilyCheckbox } from "./components/RuleBucketHints";
import { RuleName } from "./components/RuleName";
import { SubmitEditCaseRate } from "./components/SubmitEditCaseRate";
import {
  editFormScope,
  savedApplicabilityOf,
} from "./helpers/ruleApplicability";
import { bucketOpeningName, CODE_LIST_FIELDS } from "./helpers/ruleBucket";
import { RuleBucketProvider } from "./hooks/useRuleBucket";

export function EditCaseRateRulesPage() {
  useDocumentTitle("Edit Case Rate Rules");
  const { id } = useParams();
  const { data: ruleset, isLoading } = useGetCaseRateRulesetByIdQuery(id);

  const methods = useForm({
    defaultValues: {
      // CLE-2842 step 2 — applicability, provider, payer (All payers) and a Fallback's payer.
      ...editFormScope(ruleset?.data),
      // CLE-2842 — a bucket opens on its display name, so a save that leaves the name alone
      // renames nobody (see useSubmitRuleGroup).
      groupName: bucketOpeningName(ruleset?.data) ?? ruleset?.data.groupName,
      cptCodes: ruleset?.data.cptCodes,
      excludedCptCodes: ruleset?.data.excludedCptCodes,
      isPerDiem: ruleset?.data.isPerDiem ?? false,
      isBilateral: ruleset?.data.isBilateral ?? false,
      contractLanguage: ruleset?.data.contractLanguage ?? "",
    },
  });

  useEffect(() => {
    if (ruleset) {
      methods.reset({
        ...editFormScope(ruleset?.data),
        groupName: bucketOpeningName(ruleset?.data) ?? ruleset?.data.groupName,
        cptCodes: ruleset?.data.cptCodes,
        excludedCptCodes: ruleset?.data.excludedCptCodes,
        isPerDiem: ruleset?.data.isPerDiem ?? false,
        isBilateral: ruleset?.data.isBilateral ?? false,
        contractLanguage: ruleset?.data.contractLanguage ?? "",
      });
    }
  }, [ruleset, methods]);

  const savedApplicability = savedApplicabilityOf(ruleset?.data);

  if (isLoading) {
    return <LoadingSpinner />;
  }
  return (
    // CLE-2842 — the row's `bucketMembers` say which other providers and payers share this rule.
    <RuleBucketProvider
      row={ruleset?.data}
      codeListFields={CODE_LIST_FIELDS.CASE_RATE}
    >
      <FormProvider {...methods}>
        <Header name={ruleset?.data.groupName} isCaseRate />
        <ProviderAndPayerEditSection savedApplicability={savedApplicability} />
        <RuleName />
        <div className="grid">
          <PerDiemToggle />
          <BilateralToggle />
        </div>
        <CptCodes />
        <ExcludedCptCodes />
        <ContractLanguageField
          ruleType="CASE_RATE"
          ruleId={id}
          attachments={ruleset?.data.attachments}
        />
        {/* CLE-2842 Part 3 — also fix the same code list on the rules that price it differently. */}
        <CodeListFamilyCheckbox termsWord="terms" />
        <CaseRateConflictBanner excludeRulesetId={id} />
        {id && ruleset && (
          <SubmitEditCaseRate id={id} savedApplicability={savedApplicability} />
        )}
        <RuleHistory histories={ruleset?.data.history} />
      </FormProvider>
    </RuleBucketProvider>
  );
}
